import type { FormEvent } from 'react';
import { useMemo, useState } from 'react';
import type {
  CustomerManagerCustomerSummary,
  CustomerManagerRequestLifecycleResponse,
  CustomerManagerRequestLogRow,
} from '@nadoutong/contracts';
import {
  ChevronUp,
  ChevronsDownUp,
  ChevronsUpDown,
  CircleAlert,
  Filter,
  RefreshCw,
  RotateCcw,
  Waypoints,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { CustomerManagerRequestLogScope } from '../api/customerManagerWorkspace.ts';
import {
  useCustomerManagerPortfolioRequestLogs,
  useCustomerManagerRequestLifecycle,
} from '../api/customerManagerWorkspace.ts';
import { usePricing } from '../api/pricing.ts';
import {
  formatLocalTimestamp,
  formatUsdMicrosOrCentsAsCnyMetered,
} from '../components/formatters.ts';
import { LocalDateTimeInput } from '../components/LocalDateTimeInput.tsx';
import {
  isLifecycleNotRecorded,
  isTimelineUnsampled,
  LifecycleNotRecordedNotice,
  LifecycleSamplingMarker,
} from '../components/LifecycleSamplingMarker.tsx';
import { isRatingPending } from '../components/rating-status.ts';
import { StatusChip } from '../components/StatusChip.tsx';
import { CustomerCell } from '../components/identity/CustomerCell.tsx';
import { useT } from '../i18n/index.ts';
import {
  Button,
  FormField,
  Input,
  PageHeader,
  Select,
  TBody,
  THead,
  Table,
  Td,
  Th,
  Tr,
} from '../ui/index.ts';

const REQUEST_PAGE_LIMIT = 50;
const REQUEST_DEFAULT_WINDOW_DAYS = 30;
const STATUS_ANY = 'any';

function defaultRequestScope(now = Date.now()): CustomerManagerRequestLogScope {
  return {
    limit: REQUEST_PAGE_LIMIT,
    offset: 0,
    from: new Date(now - REQUEST_DEFAULT_WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString(),
    to: new Date(now).toISOString(),
    status: '',
    lifecycleStatus: '',
    requestId: '',
    apiKeyId: '',
    channelId: '',
    channelSlug: '',
  };
}

function statusFromSelect(value: string): CustomerManagerRequestLogScope['status'] {
  return value === 'success' || value === 'error' ? value : '';
}

function formatDuration(ms: number | null | undefined): string {
  return typeof ms === 'number' && Number.isFinite(ms) ? `${ms}ms` : '-';
}

function truncate(value: string, head = 10, tail = 6): string {
  if (value.length <= head + tail + 3) return value;
  return `${value.slice(0, head)}...${value.slice(-tail)}`;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function CustomerManagerRequestLogsPage(): JSX.Element {
  const t = useT();
  const initialScope = useMemo(() => defaultRequestScope(), []);
  const [draft, setDraft] = useState<CustomerManagerRequestLogScope>(initialScope);
  const [scope, setScope] = useState<CustomerManagerRequestLogScope>(initialScope);
  const [openLifecycleRequestIds, setOpenLifecycleRequestIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const logs = useCustomerManagerPortfolioRequestLogs(scope);
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;
  const rows = logs.data?.rows ?? [];
  const customers = logs.data?.customers ?? [];
  const customerByOrgId = useMemo(
    () => new Map(customers.map((customer) => [customer.org_id, customer])),
    [customers],
  );
  const total = logs.data?.total ?? 0;
  const currentOffset = logs.data?.offset ?? scope.offset;
  const hasPrev = currentOffset > 0;
  const lowerBound = logs.data?.total_is_lower_bound === true;
  const pageLimit = logs.data?.limit ?? rows.length;
  // A lower-bound total never closes the list; a short page does.
  const hasNext = rows.length >= pageLimit && rows.length > 0 && (lowerBound || currentOffset + rows.length < total);
  const traceAvailableCount = rows.filter(
    (row) => row.trace_available === true || row.trace_available === 1,
  ).length;
  const openVisibleLifecycleCount = rows.filter((row) =>
    openLifecycleRequestIds.has(row.request_id),
  ).length;

  function applyFilters(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setScope({ ...draft, offset: 0 });
  }

  function pageRequests(nextOffset: number): void {
    setDraft((prev) => ({ ...prev, offset: nextOffset }));
    setScope((prev) => ({ ...prev, offset: nextOffset }));
  }

  function resetFilters(): void {
    const next = defaultRequestScope();
    setDraft(next);
    setScope(next);
    setOpenLifecycleRequestIds(new Set());
  }

  function showErrors(): void {
    const next: CustomerManagerRequestLogScope = { ...draft, status: 'error', offset: 0 };
    setDraft(next);
    setScope(next);
  }

  function toggleLifecycle(requestId: string): void {
    setOpenLifecycleRequestIds((current) => {
      const next = new Set(current);
      if (next.has(requestId)) {
        next.delete(requestId);
      } else {
        next.add(requestId);
      }
      return next;
    });
  }

  function expandVisibleLifecycle(): void {
    setOpenLifecycleRequestIds((current) => {
      const next = new Set(current);
      for (const row of rows) next.add(row.request_id);
      return next;
    });
  }

  function collapseVisibleLifecycle(): void {
    setOpenLifecycleRequestIds((current) => {
      const next = new Set(current);
      for (const row of rows) next.delete(row.request_id);
      return next;
    });
  }

  return (
    <section data-testid="customer-manager-request-logs-page" className="flex flex-col gap-6">
      <PageHeader
        title={t('customer_manager_requests.title')}
        sub={t('customer_manager_requests.subtitle')}
        actions={
          <span className="font-mono text-micro text-paper-low">
            {logs.data?.generated_at !== undefined
              ? t('customer_manager_requests.generated_at', {
                  time: formatLocalTimestamp(logs.data.generated_at),
                })
              : t('customer_manager_requests.generated_pending')}
          </span>
        }
      />

      <section className="flex flex-col gap-4 rounded-lg border border-line-soft bg-card p-4">
        <form
          onSubmit={applyFilters}
          className="grid gap-3 md:grid-cols-2 xl:grid-cols-4"
          data-testid="customer-manager-request-log-filter-form"
        >
          <LocalDateTimeInput
            label={t('customer_manager_360.filter.from')}
            value={draft.from}
            onChange={(v) => setDraft({ ...draft, from: v })}
            testId="customer-manager-request-log-filter-from"
          />
          <LocalDateTimeInput
            label={t('customer_manager_360.filter.to')}
            value={draft.to}
            onChange={(v) => setDraft({ ...draft, to: v })}
            testId="customer-manager-request-log-filter-to"
          />
          <TextFilter
            label={t('customer_manager_360.filter.request_id')}
            value={draft.requestId}
            testId="customer-manager-request-log-filter-request-id"
            onChange={(v) => setDraft({ ...draft, requestId: v })}
          />
          <FormField
            label={t('customer_manager_360.filter.status')}
            fieldId="customer-manager-request-log-filter-status"
          >
            <Select
              value={draft.status === '' ? STATUS_ANY : draft.status}
              onValueChange={(v) => setDraft({ ...draft, status: statusFromSelect(v) })}
              options={[
                { value: STATUS_ANY, label: t('customer_manager_360.filter.any') },
                { value: 'success', label: t('customer_manager_360.filter.success') },
                { value: 'error', label: t('customer_manager_360.filter.error') },
              ]}
              ariaLabel={t('customer_manager_360.filter.status')}
              testId="customer-manager-request-log-filter-status"
            />
          </FormField>
          <TextFilter
            label={t('customer_manager_360.filter.lifecycle_status')}
            hint={t('request_lifecycle.filter_scope_note')}
            value={draft.lifecycleStatus}
            testId="customer-manager-request-log-filter-lifecycle-status"
            onChange={(v) => setDraft({ ...draft, lifecycleStatus: v })}
          />
          <TextFilter
            label={t('customer_manager_360.filter.api_key')}
            value={draft.apiKeyId}
            testId="customer-manager-request-log-filter-api-key"
            onChange={(v) => setDraft({ ...draft, apiKeyId: v })}
          />
          <TextFilter
            label={t('customer_manager_360.filter.channel_slug')}
            value={draft.channelSlug}
            testId="customer-manager-request-log-filter-channel-slug"
            onChange={(v) => setDraft({ ...draft, channelSlug: v })}
          />
          <div className="flex items-end justify-end gap-2 md:col-span-2 xl:col-span-1">
            <Button
              onClick={resetFilters}
              data-testid="customer-manager-request-log-filter-reset"
            >
              <RotateCcw aria-hidden="true" className="h-3.5 w-3.5" />
              {t('customer_manager_360.filter.reset_30d')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              data-testid="customer-manager-request-log-filter-submit"
            >
              <Filter aria-hidden="true" className="h-3.5 w-3.5" />
              {t('customer_manager_360.filter.apply')}
            </Button>
          </div>
        </form>

        <div
          data-testid="customer-manager-request-log-controls"
          className="flex flex-col gap-3 border-t border-line-soft pt-3 xl:flex-row xl:items-center xl:justify-between"
        >
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="m-0 text-label font-medium text-paper">
                {t('customer_manager_360.logs.controls.title')}
              </h3>
              <span className="rounded-full bg-raised px-2 py-0.5 text-micro font-medium text-paper-low">
                {t('customer_manager_360.logs.controls.visible', {
                  count: rows.length,
                  trace: traceAvailableCount,
                })}
              </span>
              <span className="rounded-full bg-raised px-2 py-0.5 text-micro font-medium text-paper-low">
                {t('customer_manager_requests.controls.customers', {
                  count: customers.length,
                })}
              </span>
            </div>
            <p className="m-0 text-dense text-paper-low">
              {t('customer_manager_requests.controls.hint')}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <LogControlButton
              icon={RefreshCw}
              label={t('customer_manager_360.logs.controls.refresh')}
              testId="customer-manager-request-log-control-refresh"
              disabled={logs.isFetching}
              onClick={() => void logs.refetch()}
            />
            <LogControlButton
              icon={CircleAlert}
              label={t('customer_manager_360.logs.controls.errors_only')}
              testId="customer-manager-request-log-control-errors"
              active={draft.status === 'error'}
              onClick={showErrors}
            />
            <LogControlButton
              icon={ChevronsUpDown}
              label={t('customer_manager_360.logs.controls.expand')}
              testId="customer-manager-request-log-control-expand"
              disabled={rows.length === 0 || openVisibleLifecycleCount === rows.length}
              onClick={expandVisibleLifecycle}
            />
            <LogControlButton
              icon={ChevronsDownUp}
              label={t('customer_manager_360.logs.controls.collapse')}
              testId="customer-manager-request-log-control-collapse"
              disabled={openVisibleLifecycleCount === 0}
              onClick={collapseVisibleLifecycle}
            />
          </div>
        </div>
      </section>

      {logs.isPending ? <LoadingPanel /> : null}
      {logs.isError ? (
        <ErrorBanner
          testId="customer-manager-request-logs-error"
          title={t('customer_manager_requests.error')}
          error={logs.error}
          onRetry={() => void logs.refetch()}
        />
      ) : null}

      {logs.isSuccess ? (
        <section className="flex flex-col gap-4">
          <RequestTable
            rows={rows}
            customerByOrgId={customerByOrgId}
            cnyPerUsd={cnyPerUsd}
            openLifecycleRequestIds={openLifecycleRequestIds}
            onLifecycleToggle={toggleLifecycle}
          />
          <nav className="flex items-center justify-between gap-4 text-dense text-paper-low">
            <span>
              {t('customer_manager_360.pagination.range', {
                start: total === 0 ? 0 : currentOffset + 1,
                end: Math.min(currentOffset + rows.length, total),
                total: lowerBound ? `≥${total}` : total,
              })}
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                disabled={!hasPrev || logs.isFetching}
                onClick={() => pageRequests(Math.max(0, currentOffset - REQUEST_PAGE_LIMIT))}
                data-testid="customer-manager-request-logs-pagination-prev"
              >
                {t('common.previous')}
              </Button>
              <Button
                size="sm"
                disabled={!hasNext || logs.isFetching}
                onClick={() => pageRequests(currentOffset + REQUEST_PAGE_LIMIT)}
                data-testid="customer-manager-request-logs-pagination-next"
              >
                {t('common.next')}
              </Button>
            </div>
          </nav>
        </section>
      ) : null}
    </section>
  );
}

function RequestTable({
  rows,
  customerByOrgId,
  cnyPerUsd,
  openLifecycleRequestIds,
  onLifecycleToggle,
}: {
  readonly rows: readonly CustomerManagerRequestLogRow[];
  readonly customerByOrgId: ReadonlyMap<string, CustomerManagerCustomerSummary>;
  readonly cnyPerUsd: number | null;
  readonly openLifecycleRequestIds: ReadonlySet<string>;
  readonly onLifecycleToggle: (requestId: string) => void;
}): JSX.Element {
  const t = useT();
  if (rows.length === 0) {
    return (
      <p data-testid="customer-manager-request-logs-empty" className="m-0 text-body text-paper-low">
        {t('customer_manager_360.empty.requests')}
      </p>
    );
  }
  return (
    <Table testId="customer-manager-request-logs-table">
      <THead>
        <Tr interactive={false}>
          <Th>{t('customer_manager_requests.column.customer')}</Th>
          <Th>{t('customer_manager_360.column.request')}</Th>
          <Th>{t('customer_manager_360.column.api_key')}</Th>
          <Th>{t('customer_manager_360.column.channel')}</Th>
          <Th>{t('customer_manager_360.column.model')}</Th>
          <Th>{t('customer_manager_360.column.status')}</Th>
          <Th num>{t('customer_manager_360.column.cost')}</Th>
          <Th>{t('customer_manager_360.column.created_at')}</Th>
          <Th>{t('customer_manager_360.column.logs')}</Th>
        </Tr>
      </THead>
      <TBody>
        {rows.map((row) => (
          <RequestLogRow
            key={row.id}
            row={row}
            customer={customerByOrgId.get(row.org_id) ?? null}
            cnyPerUsd={cnyPerUsd}
            lifecycleOpen={openLifecycleRequestIds.has(row.request_id)}
            onLifecycleToggle={() => onLifecycleToggle(row.request_id)}
          />
        ))}
      </TBody>
    </Table>
  );
}

function RequestLogRow({
  row,
  customer,
  cnyPerUsd,
  lifecycleOpen,
  onLifecycleToggle,
}: {
  readonly row: CustomerManagerRequestLogRow;
  readonly customer: CustomerManagerCustomerSummary | null;
  readonly cnyPerUsd: number | null;
  readonly lifecycleOpen: boolean;
  readonly onLifecycleToggle: () => void;
}): JSX.Element {
  const t = useT();
  const lifecycle = useCustomerManagerRequestLifecycle(row.org_id, row.request_id, lifecycleOpen);
  const traceAvailable = row.trace_available === true || row.trace_available === 1;
  const lifecycleStatus = row.lifecycle_status ?? '-';
  return (
    <>
      <Tr data-testid={`customer-manager-request-log-row-${row.request_id}`}>
        <Td className="align-top">
          <CustomerCell
            orgId={row.org_id}
            secondary={
              <div className="flex min-w-0 flex-col gap-1">
                <Link
                  to={`/crm/customers/${encodeURIComponent(row.org_id)}`}
                  className="max-w-56 truncate text-body font-medium text-signal transition-colors duration-120 hover:text-signal-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
                >
                  {customer?.email ?? row.org_id}
                </Link>
                <span className="max-w-56 truncate text-micro text-paper-low">
                  {customer?.org_name ?? row.org_id}
                </span>
              </div>
            }
          />
        </Td>
        <Td className="align-top">
          <div className="flex flex-col gap-1">
            <span className="font-mono text-paper">{truncate(row.request_id)}</span>
            <span className="font-mono text-micro text-paper-low">{row.request_id}</span>
          </div>
        </Td>
        <Td className="align-top">{row.api_key_name ?? row.api_key_prefix ?? truncate(row.api_key_id, 8, 4)}</Td>
        <Td className="align-top">{row.channel_slug ?? row.upstream_channel_slug ?? '-'}</Td>
        <Td mono className="align-top">{`${row.vendor}/${row.model}`}</Td>
        <Td className="align-top">
          <div className="flex flex-col items-start gap-1">
            <StatusChip variant={row.status_code >= 400 ? 'error' : 'ok'} label={row.status_code} />
            <span
              className="font-mono text-micro text-paper-low"
              title={row.lifecycle_status === null || row.lifecycle_status === undefined ? t('request_lifecycle.column_unsampled') : undefined}
            >
              {lifecycleStatus}
            </span>
            {lifecycleOpen && lifecycle.data !== undefined ? (
              <span className="font-mono text-micro text-signal">
                {lifecycle.data.metric.lastPhase}
              </span>
            ) : null}
          </div>
        </Td>
        <Td num className="align-top">
          {isRatingPending(row.rating_status) ? (
            <span className="text-paper-low">{t('customer_manager_360.cost.pending')}</span>
          ) : (
            formatUsdMicrosOrCentsAsCnyMetered(row.cost_usd_micros, row.cost_usd_cents, cnyPerUsd, false)
          )}
        </Td>
        <Td mono className="align-top">{formatLocalTimestamp(row.created_at)}</Td>
        <Td className="align-top">
          <div className="flex min-w-64 flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={onLifecycleToggle}
              data-testid={`customer-manager-request-log-row-${row.request_id}-lifecycle`}
            >
              {lifecycleOpen ? (
                <ChevronUp aria-hidden="true" className="h-3.5 w-3.5" />
              ) : (
                <Waypoints aria-hidden="true" className="h-3.5 w-3.5" />
              )}
              {lifecycleOpen
                ? t('customer_manager_360.logs.hide_lifecycle')
                : t('customer_manager_360.logs.view_lifecycle')}
            </Button>
            <StatusChip
              variant={traceAvailable ? 'ok' : 'neutral'}
              label={
                traceAvailable
                  ? t('customer_manager_360.logs.trace_available')
                  : t('customer_manager_360.logs.trace_unavailable')
              }
            />
          </div>
        </Td>
      </Tr>
      {lifecycleOpen ? (
        <Tr interactive={false}>
          <td colSpan={9} className="border-b border-line-soft px-4 pb-4">
            <RequestLifecyclePanel
              requestId={row.request_id}
              lifecycle={lifecycle.data}
              loading={lifecycle.isLoading}
              error={lifecycle.error}
            />
          </td>
        </Tr>
      ) : null}
    </>
  );
}

function RequestLifecyclePanel({
  requestId,
  lifecycle,
  loading,
  error,
}: {
  readonly requestId: string;
  readonly lifecycle: CustomerManagerRequestLifecycleResponse | undefined;
  readonly loading: boolean;
  readonly error: unknown;
}): JSX.Element {
  const t = useT();
  if (loading) {
    return (
      <div className="rounded-lg bg-well px-4 py-3 text-dense text-paper-low">
        {t('customer_manager_360.logs.lifecycle_loading')}
      </div>
    );
  }
  if (isLifecycleNotRecorded(error)) {
    return (
      <LifecycleNotRecordedNotice
        testId={`customer-manager-request-lifecycle-not-recorded-${requestId}`}
        className="rounded-lg bg-well px-4 py-3 text-dense text-paper-low"
      />
    );
  }
  if (error !== null && error !== undefined) {
    return (
      <div className="rounded-lg border border-bad-line bg-bad-dim/50 px-4 py-3 text-dense text-bad-bright">
        {t('customer_manager_360.logs.lifecycle_error')}: {errorMessage(error)}
      </div>
    );
  }
  if (lifecycle === undefined) {
    return (
      <div className="rounded-lg bg-well px-4 py-3 text-dense text-paper-low">
        {t('customer_manager_360.logs.lifecycle_empty')}
      </div>
    );
  }

  const metric = lifecycle.metric;
  const unsampled = isTimelineUnsampled(lifecycle);
  return (
    <div
      data-testid={`customer-manager-request-lifecycle-${requestId}`}
      className="sticky left-0 flex w-[calc(100vw-6rem)] max-w-[calc(100vw-6rem)] flex-col gap-3 rounded-lg bg-well p-4 lg:w-[calc(100vw-22rem)] lg:max-w-[calc(100vw-22rem)] max-md:w-[calc(100vw-2.5rem)] max-md:max-w-[calc(100vw-2.5rem)]"
    >
      <div className="flex flex-wrap items-center gap-2 text-dense text-paper-low">
        <span className="font-mono text-paper">{requestId}</span>
        <StatusChip
          variant={metric.statusCode !== null && metric.statusCode >= 400 ? 'error' : 'ok'}
          label={`${metric.lifecycleStatus}${metric.statusCode !== null ? ` ${metric.statusCode}` : ''}`}
        />
        <span className="font-mono">last {metric.lastPhase}</span>
        <LifecycleSamplingMarker
          metric={metric}
          events={lifecycle.events}
          timelineUnsampled={lifecycle.timeline_unsampled}
          testId={`customer-manager-request-lifecycle-unsampled-${requestId}`}
        />
      </div>
      <dl className="grid gap-2 md:grid-cols-2 xl:grid-cols-5">
        <LifecycleMetric label={t('customer_manager_360.logs.metric.total')} value={formatDuration(metric.totalMs)} />
        <LifecycleMetric label={t('customer_manager_360.logs.metric.upstream')} value={formatDuration(metric.upstreamDurationMs)} />
        <LifecycleMetric label={t('customer_manager_360.logs.metric.client')} value={formatDuration(metric.clientResponseDurationMs)} />
        <LifecycleMetric label={t('customer_manager_360.logs.metric.billing')} value={formatDuration(metric.billingDurationMs)} />
        <LifecycleMetric label={t('customer_manager_360.logs.metric.trace')} value={metric.traceStatus ?? '-'} />
      </dl>
      {unsampled ? null : (
        <div className="overflow-x-auto">
          <table className="min-w-[880px] w-full border-separate border-spacing-y-1">
            <thead>
              <tr className="text-left text-label font-medium text-paper-low">
                <th className="px-2 py-1 font-medium">{t('customer_manager_360.logs.phase')}</th>
                <th className="px-2 py-1 font-medium">{t('customer_manager_360.logs.event_time')}</th>
                <th className="px-2 py-1 font-medium">{t('customer_manager_360.logs.elapsed')}</th>
                <th className="px-2 py-1 font-medium">{t('customer_manager_360.logs.duration')}</th>
                <th className="px-2 py-1 font-medium">{t('customer_manager_360.logs.outcome')}</th>
              </tr>
            </thead>
            <tbody>
              {lifecycle.events.map((event) => (
                <tr key={event.eventId} className="bg-card">
                  <td className="px-2 py-2 font-mono text-dense text-paper">{event.phase}</td>
                  <td className="px-2 py-2 font-mono text-dense text-paper-low">
                    {formatLocalTimestamp(event.eventTs, { seconds: true, milliseconds: true })}
                  </td>
                  <td className="px-2 py-2 font-mono text-dense text-paper-low">
                    {formatDuration(event.elapsedMs)}
                  </td>
                  <td className="px-2 py-2 font-mono text-dense text-paper-low">
                    {formatDuration(event.durationMs)}
                  </td>
                  <td className="px-2 py-2 text-dense text-paper-low">
                    {event.outcome ?? event.errorCode ?? '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LifecycleMetric({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <div className="rounded-lg bg-card px-3 py-2">
      <dt className="text-label text-paper-low">{label}</dt>
      <dd className="m-0 mt-1 font-mono text-dense text-paper">{value}</dd>
    </div>
  );
}

function LogControlButton({
  icon: Icon,
  label,
  testId,
  active = false,
  disabled = false,
  onClick,
}: {
  readonly icon: LucideIcon;
  readonly label: string;
  readonly testId: string;
  readonly active?: boolean;
  readonly disabled?: boolean;
  readonly onClick: () => void;
}): JSX.Element {
  return (
    <Button
      size="sm"
      variant={active ? 'danger' : 'secondary'}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
    >
      <Icon aria-hidden="true" className="h-3.5 w-3.5" />
      {label}
    </Button>
  );
}

function TextFilter({
  label,
  value,
  testId,
  hint,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly testId: string;
  readonly hint?: string;
  readonly onChange: (value: string) => void;
}): JSX.Element {
  return (
    <FormField label={label} fieldId={testId} hint={hint}>
      <Input
        id={testId}
        value={value}
        data-testid={testId}
        onChange={(e) => onChange(e.currentTarget.value)}
      />
    </FormField>
  );
}

function LoadingPanel(): JSX.Element {
  return (
    <div
      data-testid="customer-manager-request-logs-loading"
      className="rounded-lg border border-line-soft bg-card px-4 py-6 text-body text-paper-low"
    >
      Loading...
    </div>
  );
}

function ErrorBanner({
  title,
  error,
  testId,
  onRetry,
}: {
  readonly title: string;
  readonly error: Error;
  readonly testId: string;
  readonly onRetry: () => void;
}): JSX.Element {
  return (
    <div
      data-testid={testId}
      className="flex items-center justify-between gap-4 rounded-lg border border-bad-line bg-bad-dim/50 px-4 py-3"
    >
      <div className="flex flex-col gap-1">
        <span className="text-body font-medium text-bad-bright">{title}</span>
        <span className="text-dense text-bad">{error.message}</span>
      </div>
      <Button variant="danger" size="sm" onClick={onRetry}>
        Retry
      </Button>
    </div>
  );
}
